import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { COLLECTIONS } from "@/integrations/firebase/collections";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import type { Request } from "./types";

export interface UseRequestsResult {
  requests: Request[];
  loading: boolean;
  error: Error | null;
}

export function useRequests(): UseRequestsResult {
  // Whose requests to show — the impersonation target's during a session
  // (`requests` read admits a super admin), the signed-in user's otherwise.
  const { actingUid } = useAuth();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset both before re-subscribing — see the note in useBuRequests. This
    // hook's dependency is `actingUid`, so the case that bites is stopping
    // impersonation: the target's subscription errors, the admin's succeeds, and
    // without this the error card outlives it.
    setError(null);
    setLoading(true);

    if (!actingUid) {
      setRequests([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, COLLECTIONS.REQUESTS),
      where("userId", "==", actingUid),
      orderBy("createdAt", "desc"),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next: Request[] = snapshot.docs.map((d) => ({
          ...(d.data() as Omit<Request, "id">),
          id: d.id,
        }));
        setRequests(next);
        setLoading(false);
      },
      (err) => {
        logError(err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [actingUid]);

  return { requests, loading, error };
}
